import { useCallback } from "react";
import { getValidAccessToken } from "../utils/authRefresh";

type Chunk = { seq: number; text: string; audio: string };

interface UseTurnBasedAssessmentSSEDeps {
  eventSourceRef: React.MutableRefObject<EventSource | null>;
  isIntroCompleteRef: React.MutableRefObject<boolean>;
  resetSequenceRef: React.MutableRefObject<(() => void) | null>;
  audioQueueRef: React.MutableRefObject<Chunk[]>;
  audioQueueCallbackRef: React.MutableRefObject<((chunk: Chunk) => void) | null>;
  processRemainingChunksRef: React.MutableRefObject<(() => void) | null>;
  lastChunkAtRef: React.MutableRefObject<number | null>;
  sseWatchdogRef: React.MutableRefObject<NodeJS.Timeout | null>;
  endTimeRef: React.MutableRefObject<number | null>;
  intervalRef: React.MutableRefObject<NodeJS.Timeout | null>;
  assessmentIdRef: React.MutableRefObject<string | null>;
  retryCountRef: React.MutableRefObject<number>;

  setAssessmentErrorModalVisible: (visible: boolean) => void;
  setQuestionIndex: (index: number) => void;
  setIsIntroComplete: (value: boolean) => void;
  setIntroText: React.Dispatch<React.SetStateAction<string | null>>;
  setIntroAudio: React.Dispatch<React.SetStateAction<string | null>>;
  setCurrentQuestionText: React.Dispatch<React.SetStateAction<string | null>>;
  setCurrentQuestionAudio: React.Dispatch<React.SetStateAction<string | null>>;
  setCanAnswer: (value: boolean) => void;
  setIsRecording: (value: boolean) => void;
  setIsProcessingAnswer: (value: boolean) => void;
  setDisplayTime: (value: string) => void;
  setRecording: (rec: any) => void;

  recording: any;

  updateRemaining: (endTime: number) => void;
  handleAssessmentComplete: () => void;
  cleanupCountdown: () => void;
  handleVisibilityChange: () => void;
  handleMouseLeave: (event: any) => void;
}

export function useTurnBasedAssessmentSSE(deps: UseTurnBasedAssessmentSSEDeps) {
  const {
    eventSourceRef,
    isIntroCompleteRef,
    resetSequenceRef,
    audioQueueRef,
    audioQueueCallbackRef,
    processRemainingChunksRef,
    lastChunkAtRef,
    sseWatchdogRef,
    endTimeRef,
    intervalRef,
    assessmentIdRef,
    retryCountRef,
    setAssessmentErrorModalVisible,
    setQuestionIndex,
    setIsIntroComplete,
    setIntroText,
    setIntroAudio,
    setCurrentQuestionText,
    setCurrentQuestionAudio,
    setCanAnswer,
    setIsRecording,
    setIsProcessingAnswer,
    setDisplayTime,
    setRecording,
    recording,
    updateRemaining,
    handleAssessmentComplete,
    cleanupCountdown,
    handleVisibilityChange,
    handleMouseLeave,
  } = deps;

  const openSSE = useCallback(
    async (assessmentId: string, endTime: number) => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }

      // Store for potential retry
      assessmentIdRef.current = assessmentId;
      endTimeRef.current = endTime;

      const token = await getValidAccessToken({ minTtlSeconds: 30 });
      if (!token) {
        console.error("No access token available for SSE connection");
        setAssessmentErrorModalVisible(true);
        return;
      }

      const url = `/api/sse-assessment/stream-assessment?assessmentId=${assessmentId}&token=${token}`;
      const es = new EventSource(url);
      eventSourceRef.current = es;

      let receivedDone = false;
      const MAX_RETRIES = 1; // Only retry once to avoid infinite loops

      es.addEventListener("state_update", (event: MessageEvent) => {
        // Reset retry count on successful connection
        retryCountRef.current = 0;
        try {
          const data = JSON.parse(event.data) as { questionIndex: number; introPlayed: boolean };
          if (data.questionIndex !== undefined) {
            setQuestionIndex(data.questionIndex + 1);
          }
          if (data.introPlayed !== undefined) {
            setIsIntroComplete(data.introPlayed);
            isIntroCompleteRef.current = data.introPlayed;
          }
        } catch (error) {
          console.error("Error parsing state_update event:", error);
        }
      });

      es.addEventListener("chunk", (event: MessageEvent) => {
        try {
          const data = JSON.parse((event as any).data) as Chunk;
          const introComplete = isIntroCompleteRef.current;

          if (introComplete) {
            if (data.seq === 1) {
              setIntroText(null);
              setIntroAudio(null);
              setCurrentQuestionText(null);
              setCurrentQuestionAudio(null);
              if (resetSequenceRef.current) {
                resetSequenceRef.current();
              }
              audioQueueRef.current = [];
            }
            setCurrentQuestionText((prev) => (prev ? `${prev}${data.text}` : data.text));
            if (audioQueueCallbackRef.current) {
              audioQueueCallbackRef.current(data);
            } else {
              audioQueueRef.current.push(data);
              setCurrentQuestionAudio(data.audio);
            }
          } else {
            setIntroText((prev) => (prev ? `${prev}${data.text}` : data.text));
            if (audioQueueCallbackRef.current) {
              audioQueueCallbackRef.current(data);
            } else {
              audioQueueRef.current.push(data);
              setIntroAudio(data.audio);
            }
          }

          lastChunkAtRef.current = Date.now();
        } catch (error) {
          console.error("Error parsing chunk event:", error);
        }
      });

      es.addEventListener("done", () => {
        receivedDone = true;
        if (audioQueueCallbackRef.current) {
          while (audioQueueRef.current.length > 0) {
            const queued = audioQueueRef.current.shift();
            if (queued) {
              audioQueueCallbackRef.current(queued);
            }
          }
        }
        if (processRemainingChunksRef.current) {
          processRemainingChunksRef.current();
        }

        if (sseWatchdogRef.current) {
          clearInterval(sseWatchdogRef.current);
          sseWatchdogRef.current = null;
        }

        const introComplete = isIntroCompleteRef.current;

        if (!introComplete) {
          setIsIntroComplete(true);
          isIntroCompleteRef.current = true;
          setTimeout(() => {
            setCanAnswer(true);
            setQuestionIndex(1);
          }, 1000);
        } else {
          setTimeout(() => {
            setCanAnswer(true);
            setIsProcessingAnswer(false);
            // Reset recording state to allow recording again
            setIsRecording(false);
            if (recording) {
              recording.stopAndUnloadAsync().catch(console.error);
              setRecording(null);
            }
          }, 1000);
        }

        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
      });

      es.addEventListener("terminate_assessment", () => {
        setCanAnswer(false);
        setIsRecording(false);
        setIsProcessingAnswer(false);
        if (recording) {
          recording.stopAndUnloadAsync().catch(console.error);
          setRecording(null);
        }
      });

      es.onerror = (event) => {
        if (es.readyState === EventSource.CLOSED && !receivedDone) {
          const retryAssessmentId = assessmentIdRef.current;
          const retryEndTime = endTimeRef.current;

          if (retryAssessmentId && retryEndTime && retryCountRef.current < MAX_RETRIES) {
            retryCountRef.current += 1;
            console.log(`SSE connection closed unexpectedly, retrying (attempt ${retryCountRef.current})...`);
            setTimeout(() => {
              if (!receivedDone && assessmentIdRef.current === retryAssessmentId) {
                openSSE(retryAssessmentId, retryEndTime);
              }
            }, 1000);
          } else {
            retryCountRef.current = 0;
            cleanupCountdown();
            setDisplayTime("00:00");
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            document.removeEventListener("mouseleave", handleMouseLeave as any);
            setAssessmentErrorModalVisible(true);
          }
        }
      };

      lastChunkAtRef.current = Date.now();
    },
    [
      cleanupCountdown,
      updateRemaining,
      handleAssessmentComplete,
      eventSourceRef,
      isIntroCompleteRef,
      resetSequenceRef,
      audioQueueRef,
      audioQueueCallbackRef,
      processRemainingChunksRef,
      lastChunkAtRef,
      sseWatchdogRef,
      intervalRef,
      assessmentIdRef,
      retryCountRef,
      endTimeRef,
      setAssessmentErrorModalVisible,
      setQuestionIndex,
      setIsIntroComplete,
      setIntroText,
      setIntroAudio,
      setCurrentQuestionText,
      setCurrentQuestionAudio,
      setCanAnswer,
      setIsRecording,
      setIsProcessingAnswer,
      setDisplayTime,
      setRecording,
      recording,
      handleVisibilityChange,
      handleMouseLeave,
    ]
  );

  return openSSE;
}

export default useTurnBasedAssessmentSSE;
